import React from 'react'

import { useSelector } from 'react-redux'

import { IResponseError } from '@lib/error'
import { IApplicationState } from '@store'

interface IProps {
  Component: React.ComponentType | React.FC
  ErrorPageComponent: React.ComponentType | React.FC
  pageProps: any
  hasError: boolean
}

export const ErrorPage: React.FC<IProps> = ({
  hasError,
  Component,
  ErrorPageComponent,
  pageProps,
}) => {
  if (hasError) {
    return <ErrorPageComponent />
  }

  return <Component {...pageProps} />
}

interface IConnected404Props {
  component: React.FC | React.ComponentType
  selector: (state: IApplicationState) => IResponseError | undefined
}

/**
 * Custom hook for conditional 404 page rendering
 * @param errorComponent - Component to render when error is detected
 * @param selector - Redux selector to determine if there's an error
 * @returns Object containing the error state
 * @example
 * const { hasError } = useConnected404({
 *   selector: selectCampaignError,
 *   errorComponent: NotFoundPage
 * })
 */
export const useConnected404 = ({
  selector,
}: Omit<IConnected404Props, 'component'>): { hasError: boolean } => {
  const error = useSelector(selector)
  return { hasError: Boolean(error) }
}

/**
 * @deprecated Use useConnected404 hook instead and implement conditional rendering
 * HOC that conditionally renders error page or component based on selector
 */
export const withConnected404Page =
  ({ component: errorComponent, selector }: IConnected404Props) =>
  (Component) =>
  (props) => {
    const hasError = useSelector((state: IApplicationState) => Boolean(selector(state)))

    return (
      <ErrorPage
        hasError={hasError}
        Component={Component}
        ErrorPageComponent={errorComponent}
        pageProps={props}
      />
    )
  }

export default withConnected404Page
